import { isMembershipLine } from '@notifi/shared-types/membership-line';
import { useCallback, useMemo } from 'react';
import {
  combineEstimateMembershipInfo,
  useEstimateMembershipInfo,
  type EstimateMembershipInfo,
} from './useEstimateMembershipInfo';
import { useMembershipPlansByIds } from '@/hooks/pricebook/useMembershipPlans';
import type { MembershipPlan } from '@/components/organisms/pricebook/support/membership-plan-shared';

/** Minimal line shape the in-cart membership detection needs. */
interface InCartMembershipLine {
  type?: string;
  /** A `membership` category makes the line a MEMBERSHIP whatever its type says. */
  taxCategory?: string | null;
  isSelected?: boolean;
  membershipPlanId?: string | null;
}

// NOT-605 — build the preview's membership context from a plan being sold
// in-cart, so a prospective (not-yet-enrolled) customer sees member rates on the
// sibling lines. Mirrors the backend buildProspectiveMembership → the
// membership-discount evaluation, so the live total matches what saves.
export function planToEstimateMembershipInfo(plan: MembershipPlan): EstimateMembershipInfo {
  return {
    planId: plan.id ?? null,
    planName: plan.name ?? null,
    // A plan discount is always a percent; the preview matches on 'PERCENT'.
    discountType: 'PERCENT',
    discountValue: Number(plan.discountPercent ?? 0),
    discountTiers: (Array.isArray(plan.discountTiers)
      ? plan.discountTiers
      : []) as EstimateMembershipInfo['discountTiers'],
    eligibleLineItemTypes: plan.eligibleLineItemTypes ?? [],
    eligibleLineItemIds: plan.eligibleLineItemIds ?? [],
    excludedLineItemIds: plan.excludedLineItemIds ?? [],
    waivers: plan.waivers ?? [],
  };
}

/**
 * NOT-605 — the estimate/job membership context for pricing. Active memberships
 * and a distinct MEMBERSHIP plan sold in-cart are evaluated together. The shared
 * pricing engine picks the best benefit per line without stacking, matching the
 * backend's evaluateDiscountAcrossMemberships path.
 *
 * `isSettled` reflects only the ACTIVE-membership lookup (used to gate upsell
 * auto-insert), unchanged from the direct hook; the in-cart context resolves
 * synchronously from the line items + plans query.
 */
/**
 * Set only when the exact in-cart plan is already ACTIVE. A different second plan
 * is valid and joins the pricing context instead of being discarded.
 */
export interface InCartMembershipOverridden {
  activePlanName: string;
  /** The in-cart plan's name, when it resolves; null while the plans query loads. */
  inCartPlanName: string | null;
}

export function useMembershipInfoWithInCart(
  contactId: string | null,
  lineItems: InCartMembershipLine[],
): {
  membershipInfo: EstimateMembershipInfo | null;
  /** Active memberships only, without the prospective plan currently in-cart. */
  activeMembershipInfo: EstimateMembershipInfo | null;
  isSettled: boolean;
  hasError: boolean;
  retry: () => void;
  inCartOverriddenByActive: InCartMembershipOverridden | null;
  /** Exact active plan identities, separate from any prospective in-cart plan. */
  activeMembershipPlanIds: string[];
} {
  const {
    membershipInfo: activeMembershipInfo,
    isSettled: activeMembershipSettled,
    hasError: activeMembershipError,
    retry: retryActiveMembership,
  } = useEstimateMembershipInfo(contactId);

  const inCartMembershipPlanId = useMemo(() => {
    // THE shared answer, the same one operations synthesizes its prospective
    // membership from: a membership says so by TYPE or by `taxCategory`. Read
    // the type alone and a service-shaped plan unlocks member rates on the
    // server while this preview quietly shows the customer list prices.
    const line = lineItems.find(
      (li) =>
        isMembershipLine({
          type: li.type == null ? null : String(li.type),
          taxCategory: li.taxCategory == null ? null : String(li.taxCategory),
        }) &&
        li.isSelected !== false &&
        Boolean(li.membershipPlanId),
    );
    return line?.membershipPlanId ?? null;
  }, [lineItems]);

  // Fetch the in-cart plan even for an existing member: a distinct second plan
  // can add complementary benefits to this document.
  const inCartMembershipPlanIds = useMemo(
    () => (inCartMembershipPlanId ? [inCartMembershipPlanId] : []),
    [inCartMembershipPlanId],
  );
  const {
    plansById: inCartPlansById,
    isSettled: inCartSettled,
    hasError: inCartError,
    retry: retryInCartMembership,
  } = useMembershipPlansByIds(inCartMembershipPlanIds, {
    enabled: inCartMembershipPlanIds.length > 0,
  });
  const inCartPlan = inCartMembershipPlanId
    ? (inCartPlansById.get(inCartMembershipPlanId) ?? null)
    : null;

  const membershipInfo = useMemo<EstimateMembershipInfo | null>(() => {
    return combineEstimateMembershipInfo([
      activeMembershipInfo,
      inCartPlan ? planToEstimateMembershipInfo(inCartPlan) : null,
    ]);
  }, [activeMembershipInfo, inCartPlan]);

  const activeMembershipPlanIds = useMemo(
    () =>
      (activeMembershipInfo?.plans?.length
        ? activeMembershipInfo.plans
        : activeMembershipInfo
          ? [activeMembershipInfo]
          : []
      ).flatMap((plan) => (plan.planId ? [plan.planId] : [])),
    [activeMembershipInfo],
  );

  // Only an exact duplicate is ignored by the backend. Surface that narrow case;
  // a distinct second plan is combined above and may improve different lines.
  const inCartOverriddenByActive = useMemo<InCartMembershipOverridden | null>(() => {
    if (!activeMembershipInfo || !inCartMembershipPlanId || !inCartPlan) return null;
    const activePlans = activeMembershipInfo.plans?.length
      ? activeMembershipInfo.plans
      : [activeMembershipInfo];
    if (!activePlans.some((plan) => plan.planId === inCartMembershipPlanId)) return null;
    return {
      activePlanName: activeMembershipInfo.planName?.trim() || 'their current plan',
      inCartPlanName: inCartPlan?.name?.trim() || null,
    };
  }, [activeMembershipInfo, inCartMembershipPlanId, inCartPlan]);

  const retry = useCallback(() => {
    if (activeMembershipError) retryActiveMembership();
    if (inCartError) retryInCartMembership();
  }, [activeMembershipError, inCartError, retryActiveMembership, retryInCartMembership]);

  return {
    membershipInfo,
    activeMembershipInfo,
    isSettled: (!contactId || activeMembershipSettled) && inCartSettled,
    hasError: Boolean((contactId && activeMembershipError) || inCartError),
    retry,
    inCartOverriddenByActive,
    activeMembershipPlanIds,
  };
}
